import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSmsWebhookList } from '@/features/webhooks/api';
import type { InboundComposeFormValues } from '../../types';
import { SmsBlock } from './shared';

interface Props {
  values: InboundComposeFormValues;
  setValues: (patch: Partial<InboundComposeFormValues>) => void;
}

export function StepNotifications({ values, setValues }: Props) {
  const smsHooks = useSmsWebhookList();
  const smsWebhookFlag = (smsHooks.data ?? []).length > 0;
  const smsOptions = (smsHooks.data ?? []).map((w) => ({
    value: String(w.id),
    label: w.webhookName,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>SMS on call outcome</CardTitle>
        <CardDescription>
          Trigger an SMS to the caller based on call result. Use an SMS webhook
          if configured; otherwise paste a gateway URL.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <SmsBlock
          flag={values.sendSmsSuccess}
          setFlag={(v) => setValues({ sendSmsSuccess: v })}
          url={values.smsApiSuccess}
          setUrl={(v) => setValues({ smsApiSuccess: v })}
          label="SMS on success"
          description="Sent when the call is answered and exceeds the success duration."
          smsWebhookFlag={smsWebhookFlag}
          smsWebhookOptions={smsOptions}
        />
        {values.sendSmsSuccess && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <Label htmlFor="cd-sms">
              Min call duration for success SMS (seconds)
            </Label>
            <Input
              id="cd-sms"
              inputMode="numeric"
              value={String(values.callDurationSMS)}
              onChange={(e) =>
                setValues({
                  callDurationSMS:
                    Number(e.target.value.replace(/\D/g, '')) || 0,
                })
              }
            />
          </div>
        )}
        <SmsBlock
          flag={values.sendSmsFail}
          setFlag={(v) => setValues({ sendSmsFail: v })}
          url={values.smsApiFail}
          setUrl={(v) => setValues({ smsApiFail: v })}
          label="SMS on failure"
          description="Sent when the call doesn't connect or hangs up too early."
          smsWebhookFlag={smsWebhookFlag}
          smsWebhookOptions={smsOptions}
        />
        <SmsBlock
          flag={values.sendSmsDtmf}
          setFlag={(v) => setValues({ sendSmsDtmf: v })}
          url={values.smsApiDtmf}
          setUrl={(v) => setValues({ smsApiDtmf: v })}
          label="SMS on DTMF input"
          description="Sent when the user presses a valid DTMF digit."
          smsWebhookFlag={smsWebhookFlag}
          smsWebhookOptions={smsOptions}
        />
      </CardContent>
    </Card>
  );
}
